import { connect, type ConnectedProps } from 'react-redux';
import { type SubmitHandler } from 'react-hook-form';
import { useCallback, useEffect, useRef, useState } from 'react';
import EventForm, { type FormValues } from '@/components/EventForm';
import { type FormElement } from '@/components/Form';
import { createEvent, tryFetchEventOptions } from '@/slices/events';
import { fetchMembers, membersByPropertyId } from '@/slices/members';
import { type RootState } from '@/store';
import { DrawerLayout } from '../../layouts';
import { type DrawerContentProps } from '../../../state';

type Props = DrawerContentProps & ConnectedProps<typeof connector>;

const CreateEventDrawer = ({
  closeDrawer,
  drawerData,
  createEvent,
  tryFetchEventOptions,
  fetchMembers,
}: Props) => {
  const formRef = useRef<FormElement>(null);
  const propertyId = drawerData?.propertyId || '';
  const preservedState = drawerData?.preservedFormState;
  const preservedEditor = drawerData?.preservedEditorValue;
  const [editorValue, setEditorValue] = useState(preservedEditor || '');

  useEffect(() => {
    tryFetchEventOptions();
    fetchMembers({ propertyId });
  }, [tryFetchEventOptions, fetchMembers, propertyId]);

  const defaultValues: FormValues = preservedState || {
    name: '',
    event_type_id: '',
    event_status_id: '',
    sport_id: '',
    contributors: [],
  };

  const handleAccept = () => {
    if (formRef.current) {
      formRef.current.submitForm();
    }
  };

  const onSubmit: SubmitHandler<FormValues> = useCallback(
    ({ name, event_type_id, sport_id, event_status_id, contributors }) => {
      createEvent({
        property_id: propertyId,
        name,
        event_type_id: Number(event_type_id),
        sport_id: sport_id ? Number(sport_id) : null,
        placeholder_content: editorValue,
        event_status_id: Number(event_status_id),
        contributors,
      });
      closeDrawer();
    },
    [closeDrawer, createEvent, propertyId, editorValue],
  );

  return (
    <DrawerLayout
      closeDrawer={closeDrawer}
      title="Create Event"
      onAccept={handleAccept}
      acceptText="Create Event"
      onCancel={closeDrawer}
      cancelText="Cancel"
    >
      <EventForm
        ref={formRef}
        editorId="create-event-editor"
        defaultValues={defaultValues}
        editorValue={editorValue}
        onEditorChange={setEditorValue}
        onSubmit={onSubmit}
        propertyId={propertyId}
      />
    </DrawerLayout>
  );
};

const connector = connect(
  (state: RootState, ownProps: { drawerData: any }) => ({
    members: membersByPropertyId(state, {
      propertyId: ownProps.drawerData?.propertyId,
    }),
  }),
  {
    fetchMembers,
    tryFetchEventOptions,
    createEvent,
  },
);

export default connector(CreateEventDrawer);
